import React from 'react';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import DatePicker from 'react-datepicker';
import UserIcon from '@material-ui/icons/SentimentVerySatisfied';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LaunchIcon from '@material-ui/icons/Launch';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CardContent from '@material-ui/core/CardContent';
import Card from '@material-ui/core/Card';
import CircularProgress from '@material-ui/core/CircularProgress';
import MaterialTable from 'material-table';
import FormControl from '@material-ui/core/FormControl';

import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import moment from 'moment';

import '../../styles/Analytics.css';
import CardData from '../CardData';

const styles = theme => ({
  containerKPIs: {
    display: 'flex',
    flexDirection: 'column',
  },
  graphKPIs: {
    width: '100%',
    display: 'flex',
  },
  cardsKPIs: {
    display: 'flex',
    marginBottom: '15px',
  },
  firstColumnHeaderContainer: {
    display: 'flex',
    alignItems: 'end',
    flexDirection: 'column',
  },
  datePickerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
  },
  calendar: {
    zIndex: 999,
  },
  dateFilter: {
    height: '30px',
    padding: '10px 5px',
    fontFamily: 'Helvetica, sans-serif',
    fontWeight: '300',
    fontSize: '16px',
    border: '1px solid #aaa',
    borderRadius: '4px',
    margin: '5px 10px 5px 10px',
  },
  paper: {
    color: theme.palette.text.secondary,
    marginBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    padding: 15,
  },
  cardTitle: {
    fontSize: 18,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  favesSelectedValue: {
    color: '#FF3B00',
    fontSize: 40,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  leagueTop1Value: {
    color: '#FF8900',
    fontSize: 40,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  leagueTop2Value: {
    color: '#EDD200',
    fontSize: 40,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  leagueTop3Value: {
    color: '#00BC2B',
    fontSize: 40,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  cardSubValue: {
    color: '#000000',
    fontSize: 18,
    marginLeft: 15,
  },
  containerData: {
    justifyContent: 'space-around',
  },
  eventsPaperCards: {
    padding: 15,
    height: 350,
    overflow: 'auto',
  },
  cards: {
    margin: 10,
  },
  tableLocation: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
});

const LineGraph = (
  title,
  data,
  xLabel,
  xAxisDataKey,
  lineDataKey,
  lineColor,
  classes
) => {
  return (
    <Paper className={classes.paper} elevation={1}>
      <Typography className={classes.subtitle} variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart
          width={500}
          height={300}
          data={data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisDataKey} />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Line name={xLabel} type="monotone" dataKey={lineDataKey} stroke={lineColor} />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

const showLoader = title => {
  return (
    <div style={{ textAlign: 'center' }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>
      <CircularProgress />
    </div>
  );
};

class KpiHome extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fieldInput: false,
      valueInput: false,
      value: '',
      momentSuggestions: [],
      momentSelected: {},
    };
  }

  onChange = (event, { newValue }) => {
    this.setState({
      value: newValue,
    });
  };

  render() {
    const {
      classes,
      activeUsers,
      newUsers,
      cohortData,
      userEngagement,
      userSession,
      totalSessionAverage,
      totalActiveUsers,
      totalNewUsers,
      totalEngagementAverage,
      activeUserRealtime,
      listProjects,
      pageViewCards,
      eventCards,
      loadingCohort,
      loadingGraphs,
      loadingPageViews,
      loadingEventClicks,
      locationSelected,
      locationData,
    } = this.props;

    const activeUserColor = '#FFA630';
    const newUserColor = '#D7E8BA';
    const engagementTimeColor = '#AFEEEE';
    const sessionColor = '#ee3b3a';
    return (
      <React.Fragment>
        <Paper className={classes.paper} elevation={0}>
          <Grid container className={classes.containerData}>
            <Grid item xs={12}>
              <Typography className={classes.subtitle} variant="subtitle1" gutterBottom>
                KPIs
              </Typography>
            </Grid>
            <Grid item xs={4} className={classes.firstColumnHeaderContainer}>
              <div className={classes.datePickerContainer}>
                Select project:
                <Select
                  value={this.props.projectSelected}
                  onChange={async event => {
                    await this.props.setProjectSelected(event.target.value);
                    this.props.refreshData();
                  }}
                >
                  {listProjects &&
                    listProjects.map((project, idx) => (
                      <MenuItem value={project} key={idx}>
                        {project}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              <div className={classes.datePickerContainer}>
                Start date:
                <DatePicker
                  popperClassName={classes.calendar}
                  className={classes.dateFilter}
                  placeholderText="Click to select a date"
                  selected={this.props.dateFrom}
                  onChange={async date => {
                    const isAfter = moment(date).isAfter(this.props.dateTo);
                    if (isAfter) {
                      await this.props.setDateTo(date);
                    }
                    await this.props.setDateFrom(date);
                    this.props.refreshData();
                  }}
                  dateFormat="MMMM d, yyyy"
                  disabledKeyboardNavigation
                />
              </div>
              <div className={classes.datePickerContainer}>
                End date:
                <DatePicker
                  popperClassName={classes.calendar}
                  className={classes.dateFilter}
                  placeholderText="Click to select a date"
                  selected={this.props.dateTo}
                  onChange={async date => {
                    await this.props.setDateTo(date);
                    this.props.refreshData();
                  }}
                  dateFormat="MMMM d, yyyy"
                  disabledKeyboardNavigation
                  minDate={this.props.dateFrom}
                />
              </div>
            </Grid>
            <Grid item>
              <div className={classes.favesSelectedValue}>{activeUserRealtime}</div>
              <div className={classes.cardTitle}>Users in the last 30 minutes</div>
            </Grid>
            <Grid item />
            <Grid item />
            <Grid item />
          </Grid>
        </Paper>

        <div className={classes.containerKPIs}>
          {loadingCohort || loadingGraphs ? (
            showLoader('Cards')
          ) : (
            <div className={classes.cardsKPIs}>
              <CardData
                label={'Total access'}
                data={totalActiveUsers}
                AvatarIcon={UserIcon}
                color={activeUserColor}
              />
              <CardData
                label={'First access'}
                data={totalNewUsers}
                AvatarIcon={TrendingUpIcon}
                color={newUserColor}
              />
              <CardData
                label={'Average engagement time'}
                data={moment.utc(totalEngagementAverage * 1000).format('HH:mm:ss')}
                AvatarIcon={AccessTimeIcon}
                color={engagementTimeColor}
              />
              <CardData
                label={'Sessions per user'}
                data={totalSessionAverage}
                AvatarIcon={LaunchIcon}
                color={sessionColor}
              />
            </div>
          )}

          {loadingCohort || loadingGraphs ? (
            showLoader('Graphs')
          ) : (
            <Grid container className={classes.graphKPIs} spacing={3}>
              <Grid item xs={6}>
                {LineGraph(
                  'Active users',
                  activeUsers,
                  'Active users',
                  'date',
                  'value',
                  activeUserColor,
                  classes
                )}
              </Grid>
              <Grid item xs={6}>
                {LineGraph(
                  'New users',
                  newUsers,
                  'New users',
                  'date',
                  'value',
                  newUserColor,
                  classes
                )}
              </Grid>
              <Grid item xs={6}>
                {LineGraph(
                  'User retention by cohort',
                  cohortData,
                  'User retention',
                  'week',
                  'value',
                  '#8884d8',
                  classes
                )}
              </Grid>
              <Grid item xs={6}>
                <Paper className={classes.paper} elevation={1}>
                  <Typography
                    className={classes.subtitle}
                    variant="subtitle1"
                    gutterBottom
                  >
                    Average engagement time
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      width={500}
                      height={300}
                      data={userEngagement}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis
                        allowDecimals={false}
                        tickFormatter={date => moment.utc(date * 1000).format('HH:mm:ss')}
                      />
                      <Tooltip
                        formatter={(value, name, props) =>
                          moment.utc(value * 1000).format('HH:mm:ss')
                        }
                      />
                      <Legend />
                      <Line
                        name={'Average engagement time'}
                        type="monotone"
                        dataKey={'value'}
                        stroke={engagementTimeColor}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                {LineGraph(
                  'Sessions per user',
                  userSession,
                  'Sessions',
                  'date',
                  'value',
                  sessionColor,
                  classes
                )}
              </Grid>
              <Grid item xs={6}>
                <Paper className={classes.paper} style={{ height: 313 }} elevation={1}>
                  <div className={classes.tableLocation}>
                    <Typography
                      className={classes.subtitle}
                      variant="subtitle1"
                      gutterBottom
                    >
                      Users by
                    </Typography>
                    <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                      <Select
                        value={locationSelected}
                        onChange={async event => {
                          await this.props.setLocationSelected(event.target.value);
                          this.props.getLocation();
                        }}
                      >
                        <MenuItem value={'country'}>Country</MenuItem>
                        <MenuItem value={'city'}>City</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <MaterialTable
                    key={locationData}
                    components={{
                      Container: props => <Paper {...props} elevation={0} />,
                    }}
                    options={{
                      pageSize: 3,
                      pageSizeOptions: [3],
                      search: false,
                      toolbar: false,
                    }}
                    columns={[
                      { title: 'Location', field: 'location' },
                      { title: 'Users', field: 'users' },
                    ]}
                    data={locationData}
                    title="Users"
                  />
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={1}>
            <Grid item xs={5}>
              {loadingPageViews ? (
                showLoader('Page views')
              ) : (
                <Paper className={classes.eventsPaperCards}>
                  <Typography variant="h5">Page views</Typography>
                  {pageViewCards &&
                    pageViewCards.map(card => (
                      <Card elevation={1} key={card.title} className={classes.cards}>
                        <CardContent>
                          <Typography variant="h5">{card.data}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {card.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                </Paper>
              )}
            </Grid>
            <Grid item xs={5}>
              {loadingEventClicks ? (
                showLoader('Event Clicks')
              ) : (
                <Paper className={classes.eventsPaperCards}>
                  <Typography variant="h5">Events</Typography>
                  {eventCards &&
                    eventCards.map(card => (
                      <Card elevation={1} key={card.title} className={classes.cards}>
                        <CardContent>
                          <Typography variant="h5">{card.data}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {card.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                </Paper>
              )}
            </Grid>
          </Grid>
        </div>
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(KpiHome);
